import { withLatestFrom, switchMap, tap } from "rxjs/operators";
import { HandlerBase } from "../../utils/HandlerBase";
import { Spatial } from "../../geo/Spatial";
import { EdgeDirection } from "../../graph/edge/EdgeDirection";
import { AbortInfra3DSDKError } from "../../error/AbortInfra3DSDKError";
import { SceneCamera } from "../../../infra3d/internal";
/**
 * The `KeySpatialNavigationHandler` allows the user to navigate through a sequence using the
 * following key commands:
 *
 * `Up Arrow`: Step forward.
 * `Down Arrow`: Step backward.
 * `Left Arrow`: Step to the left.
 * `Rigth Arrow`: Step to the right.
 * `SHIFT` + `Down Arrow`: Turn around.
 * `SHIFT` + `Left Arrow`: Turn to the left.
 * `SHIFT` + `Rigth Arrow`: Turn to the right.
 *
 * @example
 * ```
 * var keyboardComponent = viewer.getComponent("keyboard");
 *
 * keyboardComponent.keySpatialNavigation.disable();
 * keyboardComponent.keySpatialNavigation.enable();
 *
 * var isEnabled = keyboardComponent.keySpatialNavigation.isEnabled;
 * ```
 */
export class KeySpatialNavigationHandler extends HandlerBase {
    /** @ignore */
    constructor(component, container, navigator) {
        super(component, container, navigator);
    }
    _enable() {
        const spatialEdges$ = this._navigator.stateService.currentNode$.pipe(switchMap((node) => {
            return node.spatialEdges$;
        }));
        this._renderCameraSubscription = this._container.renderService.renderCamera$
            .pipe(tap((renderCamera) => {
            this.setRenderCamera(renderCamera);
        }))
            .subscribe();
        this._keyDownSubscription = this._container.keyboardService.keyDown$
            .pipe(withLatestFrom(spatialEdges$, this._navigator.stateService.currentState$))
            .subscribe(([event, edgeStatus, frame]) => {
            let direction = null;
            switch (event.keyCode) {
                case 37: // left
                    direction = event.shiftKey ? EdgeDirection.TurnLeft : EdgeDirection.StepLeft;
                    break;
                case 38: // up
                    direction = event.shiftKey ? EdgeDirection.Spherical : EdgeDirection.StepForward;
                    break;
                case 39: // right
                    direction = event.shiftKey ? EdgeDirection.TurnRight : EdgeDirection.StepRight;
                    break;
                case 40: // down
                    direction = event.shiftKey ? EdgeDirection.TurnU : EdgeDirection.StepBackward;
                    break;
                default:
                    return;
            }
            event.preventDefault();
            if (event.altKey || !edgeStatus.cached) {
                return;
            }
            if (event.shiftKey) {
                this._moveDir(direction, edgeStatus);
            }
            else {
                const shifts = {};
                shifts[EdgeDirection.StepBackward] = Math.PI;
                shifts[EdgeDirection.StepForward] = 0;
                shifts[EdgeDirection.StepLeft] = Math.PI / 2;
                shifts[EdgeDirection.StepRight] = -Math.PI / 2;
                const phi = this._rotation.phi;
                const navigationAngle = Spatial.wrapAngle(phi + shifts[direction]);
                const edges = edgeStatus.edges.filter((e) => {
                    return (e.data.direction === EdgeDirection.Spherical ||
                        [
                            EdgeDirection.StepLeft,
                            EdgeDirection.StepRight,
                            EdgeDirection.StepForward,
                            EdgeDirection.StepBackward,
                        ].indexOf(e.data.direction) != -1);
                });
                const threshold = Math.PI / edges.length;
                let smallestAngle = Number.MAX_VALUE;
                let toKey = null;
                for (const edge of edges) {
                    const angle = Math.abs(Spatial.wrapAngle(edge.data.worldMotionAzimuth - navigationAngle));
                    if (angle < Math.min(smallestAngle, threshold)) {
                        smallestAngle = angle;
                        toKey = edge.to;
                    }
                }
                if (toKey == null) {
                    return;
                }
                this._moveToKey(toKey);
            }
        });
    }
    _disable() {
        this._keyDownSubscription.unsubscribe();
        this._renderCameraSubscription.unsubscribe();
    }
    _getConfiguration(enable) {
        return { keySpatialNavigation: enable };
    }
    _moveDir(direction, edgeStatus) {
        for (const edge of edgeStatus.edges) {
            if (edge.data.direction === direction) {
                if ([EdgeDirection.StepLeft, EdgeDirection.StepRight, EdgeDirection.Spherical].indexOf(direction) == -1) {
                    this._navigator.needsResetPanoOrientation = true;
                }
                this._moveToKey(edge.to);
                return;
            }
        }
    }
    _moveToKey(key) {
        this._navigator.moveToKey$(key).subscribe(undefined, (error) => {
            if (!(error instanceof AbortInfra3DSDKError)) {
                console.error(error);
            }
        });
    }
    setRenderCamera(camera) {
        const rotation = this._rotationFromCamera(camera);
        this._rotation = rotation;
    }
    _rotationFromCamera(camera) {
        const orientation = camera.getOrientation();
        const latlon = camera.getLatLon();
        const _omega = Spatial.degToRad(orientation.x);
        const _phi = Spatial.degToRad(orientation.y);
        const x = -1 * Math.sin(_phi);
        const y = Math.sin(_omega) * Math.cos(_phi);
        const longitude = Spatial.degToRad(latlon[1]);
        const latitude = Spatial.degToRad(latlon[0]);
        const fov = Spatial.degToRad(latlon[2]);
        const phi = Math.atan2(y, x) - longitude;
        let theta = latitude - fov / 2;
        if (camera instanceof SceneCamera) {
            theta = Spatial.degToRad(-40);
        }
        return { phi: phi, theta: theta };
    }
}
//# sourceMappingURL=KeySpatialNavigationHandler.js.map